import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabase";
import { useModal } from "../../context/ModalContext";
import Select from "../../components/Select";

const API = process.env.REACT_APP_API_URL || "";

export default function AdminSubscribers() {
  const { showAlert, showConfirm, showPrompt } = useModal();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("active");
  const [filterProvider, setFilterProvider] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refundingId, setRefundingId] = useState(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [mainTab, setMainTab] = useState("subscribers");
  const [refundRequests, setRefundRequests] = useState([]);
  const [refundRequestsLoading, setRefundRequestsLoading] = useState(false);

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("subscriptions")
        .select("*, profiles!subscriptions_user_id_fkey(email, role, plan)")
        .order("created_at", { ascending: false })
        .limit(200);

      if (filterStatus) query = query.eq("status", filterStatus);
      if (filterProvider) query = query.eq("provider", filterProvider);

      const { data, error } = await query;
      if (error) {
        console.warn("subscriptions join failed, fallback:", error);
        const { data: fb } = await supabase
          .from("subscriptions")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(200);
        setSubscribers(fb || []);
      } else {
        setSubscribers(data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterProvider]);

  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);

  const fetchRefundRequests = useCallback(async () => {
    setRefundRequestsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API}/api/admin/refund-requests`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRefundRequests(data || []);
      }
    } catch (e) { console.error(e); }
    finally { setRefundRequestsLoading(false); }
  }, []);

  useEffect(() => {
    if (mainTab === "refunds") fetchRefundRequests();
  }, [mainTab, fetchRefundRequests]);

  async function loadDetail(sub) {
    setSelected(sub);
    setDetail(null);
    setSelectedPaymentId(null);
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/subscription-usage/${sub.user_id}`);
      const data = await res.json();
      if (!res.ok) {
        showAlert(data.error || "사용량 조회 실패");
        return;
      }
      setDetail(data);
      // 기본적으로 가장 최근 회차 선택 (payments는 최신 순으로 정렬되어 옴)
      if (data.payments && data.payments.length > 0) {
        setSelectedPaymentId(data.payments[0].id);
      }
    } catch (e) {
      console.error(e);
      showAlert("조회 실패");
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleRefund(payment, onSuccess) {
    // refund_reason에서 일할 계산 금액 파싱
    const storedReason = payment.refund_reason || "";
    const amountMatch = storedReason.match(/\[환불금액:(\d+)원\]/);
    const proRataAmount = amountMatch ? parseInt(amountMatch[1], 10) : null;
    const isProRata = proRataAmount !== null && proRataAmount !== payment.amount_krw;

    const amountInfo = isProRata
      ? `결제금액: ${(payment.amount_krw || 0).toLocaleString()}원 → 환불금액(일할): ${proRataAmount.toLocaleString()}원`
      : `금액: ${(payment.amount_krw || 0).toLocaleString()}원 (전액)`;

    const reason = await showPrompt(
      `환불 사유를 입력하세요\n(${amountInfo}, provider: ${payment.provider})`,
      "관리자 환불 처리"
    );
    if (!reason) return;
    if (!(await showConfirm(
      `정말 환불하시겠습니까?\n\n${amountInfo}\n사유: ${reason}\n\n이 작업은 되돌릴 수 없습니다.`
    ))) return;

    setRefundingId(payment.id);
    const requestBody = { reason };
    if (isProRata) requestBody.amount = proRataAmount;

    try {
      const res = await fetch(`${API}/api/subscriptions/payments/${payment.id}/refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const data = await res.json();
      if (!res.ok) {
        showAlert(`환불 실패: ${data.error || JSON.stringify(data.details || {})}`);
        return;
      }
      showAlert("환불 완료");
      if (onSuccess) onSuccess();
      else if (selected) loadDetail(selected);
    } catch (e) {
      showAlert("환불 요청 오류: " + e.message);
    } finally {
      setRefundingId(null);
    }
  }

  const filtered = subscribers.filter((s) => {
    if (!search) return true;
    const email = s.profiles?.email || "";
    const userId = s.user_id || "";
    const q = search.toLowerCase();
    return email.toLowerCase().includes(q) || userId.toLowerCase().includes(q);
  });

  function fmtDate(s) {
    if (!s) return "-";
    try { return new Date(s).toLocaleString("ko-KR"); } catch { return s; }
  }
  function fmtDateShort(s) {
    if (!s) return "-";
    try { return new Date(s).toLocaleDateString("ko-KR"); } catch { return s; }
  }
  function fmtKRW(n) {
    return (n || 0).toLocaleString() + "원";
  }

  return (
    <div className="admin-page">
      <h2 className="admin-title">구독자 관리</h2>

      <div className="admin-tabs" style={{ marginBottom: 16 }}>
        <button
          className={`admin-tab ${mainTab === "subscribers" ? "active" : ""}`}
          onClick={() => setMainTab("subscribers")}
        >
          구독자 목록
        </button>
        <button
          className={`admin-tab ${mainTab === "refunds" ? "active" : ""}`}
          onClick={() => setMainTab("refunds")}
        >
          환불 신청 {refundRequests.length > 0 && `(${refundRequests.length})`}
        </button>
      </div>

      {mainTab === "refunds" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
            <button className="admin-btn" onClick={fetchRefundRequests}>새로고침</button>
          </div>
          <div className="admin-card" style={{ padding: 0 }}>
            {refundRequestsLoading ? (
              <div style={{ padding: 24, textAlign: "center", color: "#A8968A" }}>로딩...</div>
            ) : refundRequests.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "#A8968A" }}>환불 신청 없음</div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>신청일</th>
                    <th>이메일</th>
                    <th>플랜</th>
                    <th>PG</th>
                    <th style={{ textAlign: "right" }}>결제금액</th>
                    <th style={{ textAlign: "right" }}>환불금액</th>
                    <th>결제일</th>
                    <th>신청 사유</th>
                    <th style={{ textAlign: "center" }}>처리</th>
                  </tr>
                </thead>
                <tbody>
                  {refundRequests.map((p) => {
                    const reasonStr = p.refund_reason || "";
                    const amountMatch = reasonStr.match(/\[환불금액:(\d+)원\]/);
                    const refundAmt = amountMatch ? parseInt(amountMatch[1], 10) : p.amount_krw;
                    const isPartial = refundAmt !== p.amount_krw;
                    return (
                    <tr key={p.id}>
                      <td style={{ fontSize: 12 }}>{fmtDateShort(p.updated_at || p.created_at)}</td>
                      <td>{p.profiles?.email || p.user_id?.slice(0, 8) + "..."}</td>
                      <td><span className={`admin-badge ${p.plan === "PRO" ? "purple" : "blue"}`}>{p.plan || "-"}</span></td>
                      <td style={{ fontSize: 12, color: "#7C6655" }}>{p.provider}</td>
                      <td style={{ textAlign: "right", color: "#A8968A" }}>{fmtKRW(p.amount_krw)}</td>
                      <td style={{ textAlign: "right", fontWeight: 600, color: isPartial ? "#f59e0b" : "inherit" }}>
                        {fmtKRW(refundAmt)}{isPartial && " (일할)"}
                      </td>
                      <td style={{ fontSize: 12 }}>{fmtDateShort(p.paid_at)}</td>
                      <td style={{ maxWidth: 180, fontSize: 12, color: "#6B5646", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {reasonStr.replace(/\[환불금액:\d+원\]\[[\w]+\]\s*/g, "") || "-"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          className="admin-btn danger"
                          style={{ padding: "5px 12px", fontSize: 12 }}
                          disabled={refundingId === p.id}
                          onClick={() => handleRefund(p, fetchRefundRequests)}
                        >
                          {refundingId === p.id ? "처리중..." : "승인 환불"}
                        </button>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {mainTab === "subscribers" && <div><div className="admin-filter-row">
        <Select className="admin-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">전체 상태</option>
          <option value="active">활성</option>
          <option value="cancelled">취소됨</option>
        </Select>
        <Select className="admin-select" value={filterProvider} onChange={(e) => setFilterProvider(e.target.value)}>
          <option value="">전체 PG</option>
          <option value="paypal">PayPal</option>
          <option value="toss">Toss</option>
          <option value="portone">PortOne</option>
        </Select>
        <input
          type="text"
          placeholder="이메일 또는 user_id 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 240, padding: "7px 12px", border: "1px solid #E7DAC8", borderRadius: 8, fontSize: 13, outline: "none" }}
        />
        <button className="admin-btn" onClick={fetchSubscribers}>새로고침</button>
        <span className="admin-count">총 {filtered.length}명</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(420px, 1fr) minmax(520px, 1.4fr)", gap: 20 }}>
        {/* 좌측: 구독자 목록 */}
        <div className="admin-section" style={{ padding: 0, maxHeight: "75vh", overflowY: "auto" }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: "center", color: "#A8968A" }}>로딩...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "#A8968A" }}>구독자 없음</div>
          ) : (
            <table className="admin-table" style={{ border: "none", borderRadius: 0 }}>
              <thead>
                <tr>
                  <th>이메일</th>
                  <th style={{ textAlign: "center" }}>플랜</th>
                  <th style={{ textAlign: "center" }}>PG</th>
                  <th style={{ textAlign: "center" }}>상태</th>
                  <th style={{ textAlign: "center" }}>시작일</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => loadDetail(s)}
                    className={selected?.id === s.id ? "selected-row" : ""}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{s.profiles?.email || s.user_id?.slice(0, 8) + "..."}</td>
                    <td style={{ textAlign: "center" }}>
                      <span className={`admin-badge ${s.plan === "PRO" ? "purple" : "blue"}`}>{s.plan}</span>
                    </td>
                    <td style={{ textAlign: "center", fontSize: 12, color: "#7C6655" }}>{s.provider}</td>
                    <td style={{ textAlign: "center" }}>
                      <span className={`admin-badge ${s.status === "active" ? "green" : "red"}`}>
                        {s.status === "active" ? "활성" : "취소"}
                      </span>
                    </td>
                    <td style={{ textAlign: "center", fontSize: 12, color: "#7C6655" }}>
                      {fmtDateShort(s.current_period_start || s.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 우측: 디테일 패널 */}
        <div className="admin-section" style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {!selected ? (
            <div style={{ color: "#A8968A", textAlign: "center", padding: 60 }}>
              좌측에서 구독자를 선택하세요
            </div>
          ) : detailLoading ? (
            <div style={{ padding: 20, color: "#A8968A" }}>로딩...</div>
          ) : !detail ? (
            <div style={{ padding: 20, color: "#A8968A" }}>데이터 없음</div>
          ) : (
            <>
              {/* 구독 정보 */}
              <h3>구독 정보</h3>
              <div style={{ fontSize: 13, marginBottom: 22, lineHeight: 1.9, color: "#4A3628" }}>
                <div><b style={{ color: "#7C6655" }}>이메일:</b> {selected.profiles?.email || "-"}</div>
                <div style={{ fontSize: 11, color: "#A8968A", fontFamily: "monospace" }}>
                  {selected.user_id}
                </div>
                <div style={{ marginTop: 6 }}>
                  <span className={`admin-badge ${detail.subscription?.plan === "PRO" ? "purple" : "blue"}`}>
                    {detail.subscription?.plan}
                  </span>
                  &nbsp;
                  <span className={`admin-badge ${detail.subscription?.status === "active" ? "green" : "red"}`}>
                    {detail.subscription?.status}
                  </span>
                  &nbsp;
                  <span style={{ fontSize: 12, color: "#7C6655" }}>
                    via {detail.subscription?.provider}
                  </span>
                </div>
                <div style={{ marginTop: 8 }}>
                  <b style={{ color: "#7C6655" }}>시작:</b> {fmtDate(detail.subscription?.current_period_start)}
                </div>
                <div>
                  <b style={{ color: "#7C6655" }}>다음 결제 예정:</b> {fmtDate(detail.subscription?.current_period_end)}
                </div>
                {detail.subscription?.cancelled_at && (
                  <div>
                    <b style={{ color: "#7C6655" }}>취소일:</b> {fmtDate(detail.subscription?.cancelled_at)}
                  </div>
                )}
              </div>

              {/* 결제 회차 (먼저 표시 — 회차를 선택하면 아래 사용량이 그 회차 기준으로 갱신) */}
              <h3>결제 회차 / 환불 <span style={{ fontSize: 12, fontWeight: 400, color: "#A8968A" }}>(클릭하면 해당 회차 사용량 표시)</span></h3>
              {detail.payments && detail.payments.length > 0 ? (
                <table className="admin-table" style={{ marginBottom: 22 }}>
                  <thead>
                    <tr>
                      <th>결제일</th>
                      <th style={{ textAlign: "right" }}>금액</th>
                      <th style={{ textAlign: "center" }}>주기 사용량 (DL/AI/갤러리)</th>
                      <th style={{ textAlign: "center" }}>상태</th>
                      <th style={{ textAlign: "center" }}>처리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.payments.map((p) => (
                      <tr
                        key={p.id}
                        className={selectedPaymentId === p.id ? "selected-row" : ""}
                        onClick={() => setSelectedPaymentId(p.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <td style={{ fontSize: 12 }}>{fmtDate(p.paid_at)}</td>
                        <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtKRW(p.amount_krw)}</td>
                        <td style={{ textAlign: "center", fontSize: 12, color: "#6B5646" }}>
                          {p.usage ? (
                            <span>
                              <b>{p.usage.download_count}</b> / <b>{p.usage.ai_design_count}</b> / <b>{p.usage.gallery_count}</b>
                            </span>
                          ) : (
                            <span style={{ color: "#D9C7B0" }}>-</span>
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {p.status === "paid" ? (
                            <span className="admin-badge green">결제완료</span>
                          ) : p.status === "refunded" ? (
                            <span className="admin-badge red">환불됨</span>
                          ) : p.status === "partial_refunded" ? (
                            <span className="admin-badge red">일할환불됨</span>
                          ) : p.status === "refund_requested" ? (
                            <span className="admin-badge yellow">환불 신청</span>
                          ) : (
                            <span className="admin-badge yellow">{p.status}</span>
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {(p.status === "paid" || p.status === "refund_requested") ? (
                            <button
                              className="admin-btn danger"
                              onClick={(e) => { e.stopPropagation(); handleRefund(p); }}
                              disabled={refundingId === p.id}
                              style={{ padding: "5px 12px", fontSize: 12 }}
                              title={p.status === "refund_requested" ? `사용자 신청 사유: ${p.refund_reason || "-"}` : ""}
                            >
                              {refundingId === p.id ? "처리중..." : (p.status === "refund_requested" ? "승인 환불" : "환불")}
                            </button>
                          ) : (
                            <span style={{ color: "#A8968A", fontSize: 11 }}>
                              {p.refunded_at ? fmtDateShort(p.refunded_at) : "-"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ color: "#A8968A", padding: 14, fontSize: 13, background: "#FBF6EE", borderRadius: 8, marginBottom: 22 }}>
                  결제 회차 기록 없음
                  <div style={{ fontSize: 11, color: "#D9C7B0", marginTop: 4 }}>
                    DB 마이그레이션 미적용 또는 마이그레이션 이전 결제. PG 콘솔에서 수동 환불 필요.
                  </div>
                </div>
              )}

              {/* 선택된 회차 사용량 통계 */}
              {(() => {
                const selectedPayment = detail.payments?.find((p) => p.id === selectedPaymentId);
                const usage = selectedPayment?.usage;
                return (
                  <>
                    <h3>
                      {selectedPayment
                        ? `${fmtDateShort(selectedPayment.paid_at)} 회차 사용량`
                        : "선택된 회차 사용량"}
                      {usage?.period_start && usage?.period_end && (
                        <span style={{ fontSize: 11, fontWeight: 400, color: "#A8968A", marginLeft: 8 }}>
                          ({fmtDateShort(usage.period_start)} ~ {fmtDateShort(usage.period_end)})
                        </span>
                      )}
                    </h3>
                    <div className="admin-cards" style={{ gridTemplateColumns: "repeat(2, 1fr)", marginBottom: 8 }}>
                      <div className="admin-card">
                        <h3>STL/갤러리 다운로드</h3>
                        <div className="big">{usage?.download_count ?? "-"}</div>
                      </div>
                      <div className="admin-card">
                        <h3>AI 디자인 생성</h3>
                        <div className="big">{usage?.ai_design_count ?? "-"}</div>
                      </div>
                      <div className="admin-card">
                        <h3>갤러리 작성</h3>
                        <div className="big">{usage?.gallery_count ?? "-"}</div>
                      </div>
                      <div className="admin-card">
                        <h3>저장공간 (현재 시점)</h3>
                        <div className="big">
                          {detail.storage?.storage_mb ?? 0}
                          <span style={{ fontSize: 14, color: "#A8968A", marginLeft: 4 }}>MB</span>
                        </div>
                        <div style={{ fontSize: 11, color: "#A8968A", marginTop: 4 }}>
                          {detail.storage?.file_count ?? 0}개 파일 (회차 무관)
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </div>}
    </div>
  );
}
